import React, { Suspense, useEffect, type FC } from 'react';

import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { expVal } from '@confluence/feature-experiments';
import { LoadablePaint } from '@confluence/loadable';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type CommentTypes = {
	pageWidthType?: string;
	showLabels: boolean;
	hasReactions: boolean;
	editorIsUserOffline?: boolean;
	hasCommentSessions?: boolean;
};

const ObjectCommentContainerLoader = LoadablePaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectCommentContainer" */ './ObjectCommentContainer.tsx'
			)
		).ObjectCommentContainer,
});

export const ObjectCommentWrapper: FC<CommentTypes> = ({
	pageWidthType,
	showLabels,
	hasReactions,
	editorIsUserOffline,
	hasCommentSessions,
}) => {
	const { setShowGeneralComments } = useCommentsPanelActions();

	useEffect(() => {
		setShowGeneralComments(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to set the comment type once on the comment mount
	}, []);

	const content = (
		<ObjectCommentContainerLoader
			pageWidthType={pageWidthType}
			showLabels={showLabels}
			hasReactions={hasReactions}
			editorIsUserOffline={editorIsUserOffline}
			hasCommentSessions={hasCommentSessions}
		/>
	);

	return (
		<SuspenseWithDataFetcher dataFetcherName="object-comments">
			{/* The streaming wrapper is a passthrough after SPA navigation. Fragment reads in
			    the container still need a boundary during synchronous footer interactions. */}
			{!StatsigConfigurations.isKillSwitchOn('confluence_vp_comment_threads_frag_reader') &&
			expVal('confluence_exp_conversation_contract', 'isEnabled', false) ? (
				<Suspense fallback={<CommentPlaceholder showPlaceholder />}>{content}</Suspense>
			) : (
				content
			)}
		</SuspenseWithDataFetcher>
	);
};
